import { createLogger } from '@/lib/logger'
import { toast } from 'sonner'

import { withNativeProgress } from './native-progress'
import { isTauri, openExternal } from './tauri'

const logger = createLogger('lib/utils/download')

/**
 * Where a downloaded file ended up.
 *
 * Only the desktop app can name a path: it owns the save dialog, so it knows
 * exactly what the user picked. In the browser the download is handed to the
 * browser's own download manager and the page never learns the destination.
 */
export type SaveOutcome =
  | { status: 'saved'; path: string }
  | { status: 'downloaded' }
  | { status: 'cancelled' }

/** `printBlob` can also hand the file straight to a viewer. */
export type PrintOutcome = SaveOutcome | { status: 'printed' }

/**
 * Bytes to save, or a thunk that produces them.
 *
 * The thunk form exists so the desktop save dialog can be shown *before* the
 * bytes are fetched. Cancelling a large download should cost no bandwidth.
 */
export type BlobSource = Blob | (() => Promise<Blob>)

/**
 * Native save-dialog filters derived from the file extension.
 *
 * Only a hint for the dialog — the user can always override it — so an unknown
 * extension falls back to "All Files" rather than blocking the save.
 */
const FILE_TYPE_NAMES: Record<string, string> = {
  csv: 'CSV',
  json: 'JSON',
  pdf: 'PDF',
  txt: 'Text',
  xlsx: 'Excel Workbook',
  xls: 'Excel Workbook',
  ics: 'Calendar',
  zip: 'Archive',
  png: 'PNG Image',
  jpg: 'JPEG Image',
  jpeg: 'JPEG Image',
  gif: 'GIF Image',
  webp: 'WebP Image',
  svg: 'SVG Image',
  doc: 'Word Document',
  docx: 'Word Document',
}

function filtersFor(
  filename: string,
): Array<{ name: string; extensions: string[] }> {
  const extension = filename.split('.').pop()?.toLowerCase()
  if (!extension || extension === filename.toLowerCase()) {
    return [{ name: 'All Files', extensions: ['*'] }]
  }
  return [
    {
      name: FILE_TYPE_NAMES[extension] || extension.toUpperCase(),
      extensions: [extension],
    },
    { name: 'All Files', extensions: ['*'] },
  ]
}

/**
 * The only anchor-based download in the codebase.
 *
 * This idiom is a no-op inside the Tauri webview — nothing in the desktop main
 * process intercepts downloads — so it must never be reached there. `saveBlob`
 * is the sole caller and it guards on `isTauri()` first.
 */
async function anchorDownload(blob: Blob, filename: string): Promise<void> {
  const objectUrl = URL.createObjectURL(blob)
  try {
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } finally {
    // The click is synchronous but the browser reads the URL asynchronously, so
    // revoking immediately can race a large file.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 100)
  }
}

async function resolveBlob(source: BlobSource): Promise<Blob> {
  return typeof source === 'function' ? await source() : source
}

async function fetchBlob(url: string): Promise<Blob> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(
      `Failed to fetch file: ${response.status} ${response.statusText}`,
    )
  }
  return response.blob()
}

function filenameFromUrl(url: string): string {
  try {
    const path = new URL(url, window.location.href).pathname
    return (
      decodeURIComponent(path.split('/').filter(Boolean).pop() || '') ||
      'download'
    )
  } catch {
    return 'download'
  }
}

/**
 * A fetch that never reached the server — DNS, offline, or a CORS rejection.
 *
 * `fetch` reports all of these as a bare TypeError; an HTTP error status comes
 * back through `fetchBlob` as a normal Error instead, so this stays specific.
 */
function isNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError
}

/**
 * Save bytes to disk. **This is the single place the desktop/browser split for
 * downloads is decided** — every download in the app must route through here.
 *
 * Throws on failure; returns `cancelled` only when the user dismissed the
 * desktop save dialog.
 */
export async function saveBlob(
  source: BlobSource,
  filename: string,
  assertCurrent?: () => void,
): Promise<SaveOutcome> {
  assertCurrent?.()
  if (isTauri()) {
    const { save } = await import('@tauri-apps/plugin-dialog')
    const savePath = await save({
      defaultPath: filename,
      title: 'Save file',
      filters: filtersFor(filename),
    })

    // Bail before touching `source` so a cancelled save skips the fetch.
    if (!savePath) return { status: 'cancelled' }

    return withNativeProgress(async () => {
      const blob = await resolveBlob(source)
      const { writeFile } = await import('@tauri-apps/plugin-fs')
      const bytes = new Uint8Array(await blob.arrayBuffer())
      assertCurrent?.()
      await writeFile(savePath, bytes)
      logger.info('File saved to:', savePath)
      return { status: 'saved', path: savePath }
    })
  }

  const blob = await resolveBlob(source)
  assertCurrent?.()
  await anchorDownload(blob, filename)
  return { status: 'downloaded' }
}

/**
 * Collapse the user's home directory to `~` so the path stays readable in a
 * toast. Falls back to the full path if the home directory can't be resolved.
 */
async function toDisplayPath(path: string): Promise<string> {
  try {
    const { homeDir } = await import('@tauri-apps/api/path')
    const home = (await homeDir()).replace(/[/\\]+$/, '')
    if (home && path.startsWith(home)) {
      return `~${path.slice(home.length)}`
    }
  } catch (error) {
    logger.warn('Failed to resolve home directory for display path:', error)
  }
  return path
}

/** Open the saved file's folder in Finder / File Explorer. */
async function revealSavedFile(path: string): Promise<void> {
  try {
    const { revealItemInDir } = await import('@tauri-apps/plugin-opener')
    await revealItemInDir(path)
  } catch (error) {
    logger.error('Failed to reveal downloaded file:', error)
    toast.error('Could not open the containing folder')
  }
}

/**
 * Tell the user where a downloaded file landed.
 *
 * Desktop only by design. A browser download already surfaces its destination
 * in the browser's own download UI, and a cancelled save needs no announcing,
 * so both stay silent rather than adding noise.
 */
export async function notifySaveOutcome(outcome: SaveOutcome): Promise<void> {
  if (outcome.status !== 'saved') return

  toast.success('Download complete', {
    description: await toDisplayPath(outcome.path),
    // Sticky: where a file landed is worth keeping on screen until the user is
    // done with it. `closeButton` is required alongside this, otherwise a toast
    // that never times out has no obvious way to dismiss it.
    duration: Infinity,
    closeButton: true,
    action: {
      label: 'Show in folder',
      onClick: () => {
        void revealSavedFile(outcome.path)
      },
    },
  })
}

/**
 * `saveBlob` plus a toast naming the save location on desktop.
 *
 * This is the default entry point for downloads of bytes the app already holds.
 * Reach for `saveBlob` directly only when the caller needs to react to the
 * outcome instead of just reporting it.
 */
export async function saveBlobWithFeedback(
  source: BlobSource,
  filename: string,
  assertCurrent?: () => void,
): Promise<SaveOutcome> {
  const outcome = await saveBlob(source, filename, assertCurrent)
  await notifySaveOutcome(outcome)
  return outcome
}

/**
 * Download a file from a URL.
 *
 * On desktop the bytes are fetched from the webview, which is CORS-gated. If
 * that fetch never reaches the server we hand the URL to the system browser
 * rather than leaving the user with a button that does nothing — presigned URLs
 * carry their own auth, so this always works.
 */
export async function downloadFile(
  url: string,
  filename?: string,
): Promise<SaveOutcome> {
  const name = filename || filenameFromUrl(url)
  try {
    return await saveBlob(() => fetchBlob(url), name)
  } catch (error) {
    if (isTauri() && isNetworkFailure(error)) {
      logger.warn(
        'Direct fetch failed, opening in the system browser instead:',
        error,
      )
      await openExternal(url)
      toast.info('Opened in your browser', {
        description: 'This file could not be saved directly from the app.',
      })
      return { status: 'downloaded' }
    }
    logger.error('Failed to download file:', error)
    throw error
  }
}

/**
 * `downloadFile` plus a toast naming the save location on desktop.
 *
 * This is the default entry point for URL-based downloads.
 */
export async function downloadFileWithFeedback(
  url: string,
  filename?: string,
): Promise<SaveOutcome> {
  const outcome = await downloadFile(url, filename)
  await notifySaveOutcome(outcome)
  return outcome
}

/**
 * Hand a document to the user for printing.
 *
 * Tauri v2 exposes no print API and `window.print()` is unreliable in WKWebView,
 * so desktop saves the file and opens it in the OS viewer, where the system
 * print dialog is one keystroke away. The web path keeps the popup-and-print
 * flow.
 */
export async function printBlob(
  source: BlobSource,
  filename: string,
): Promise<PrintOutcome> {
  if (isTauri()) {
    const outcome = await saveBlob(source, filename)
    if (outcome.status !== 'saved') return outcome

    try {
      const { openPath } = await import('@tauri-apps/plugin-opener')
      await openPath(outcome.path)
      toast.success('Opened in your PDF viewer', {
        description: 'Press Cmd+P there to print.',
      })
      return { status: 'printed' }
    } catch (error) {
      logger.error('Failed to open the saved file for printing:', error)
      await notifySaveOutcome(outcome)
      return outcome
    }
  }

  const blob = await resolveBlob(source)
  const objectUrl = URL.createObjectURL(blob)
  const printWindow = window.open(objectUrl, '_blank')
  if (!printWindow) {
    URL.revokeObjectURL(objectUrl)
    throw new Error('Unable to open a print window')
  }

  // Revoke once the popup is finished with the URL. A fixed timer can pull the
  // document out from under a large PDF that is still loading.
  const release = () => URL.revokeObjectURL(objectUrl)
  printWindow.addEventListener('afterprint', release, { once: true })
  printWindow.addEventListener('unload', release, { once: true })

  printWindow.focus()
  printWindow.print()
  return { status: 'printed' }
}
